import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { PlusIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useConfirm } from "../contexts/ConfirmContext";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

const Exams = () => {
  const { confirm } = useConfirm();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedExam, setSelectedExam] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    examDate: "",
    duration: "",
    mode: "Online",
    location: "",
    maxParticipants: "",
  });

  useEffect(() => {
    fetchExams();
  }, []);

  const fetchExams = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/exams`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExams(response.data);
    } catch (error) {
      toast.error("Failed to fetch exams");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const examData = {
        ...formData,
        examDate: new Date(formData.examDate).toISOString(),
        duration: parseInt(formData.duration),
        maxParticipants: parseInt(formData.maxParticipants),
      };
      if (selectedExam) {
        await axios.put(`${API_URL}/exams/${selectedExam.id}`, examData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Exam updated successfully");
      } else {
        await axios.post(`${API_URL}/exams`, examData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Exam created successfully");
      }
      setShowForm(false);
      setSelectedExam(null);
      setFormData({
        title: "",
        description: "",
        category: "",
        examDate: "",
        duration: "",
        mode: "Online",
        location: "",
        maxParticipants: "",
      });
      fetchExams();
    } catch (error) {
      toast.error("Failed to save exam");
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: "Delete exam",
      message: "Are you sure you want to delete this exam?",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/exams/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Exam deleted successfully");
      if (selectedExam && selectedExam.id === id) setSelectedExam(null);
      fetchExams();
    } catch (error) {
      toast.error("Failed to delete exam");
    }
  };

  const handleEdit = (exam) => {
    setSelectedExam(exam);
    setFormData({
      title: exam.title,
      description: exam.description,
      category: exam.category,
      examDate: new Date(exam.examDate).toISOString().slice(0, 16),
      duration: exam.duration.toString(),
      mode: exam.mode,
      location: exam.location || "",
      maxParticipants: exam.maxParticipants.toString(),
    });
    setShowForm(true);
  };

  const filteredExams = exams.filter(
    (exam) =>
      exam.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      exam.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex h-[calc(100vh-64px)] bg-gray-50 rounded-lg shadow overflow-hidden">
      {/* Left Column: Exam List */}
      <div className="w-1/4 min-w-[260px] bg-white border-r flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <span className="font-semibold text-lg">Exams</span>
          <button
            className="flex items-center gap-1 px-2 py-1 text-sm bg-teal-600 text-white rounded hover:bg-teal-700"
            onClick={() => {
              setShowForm(true);
              setSelectedExam(null);
              setFormData({
                title: "",
                description: "",
                category: "",
                examDate: "",
                duration: "",
                mode: "Online",
                location: "",
                maxParticipants: "",
              });
            }}
          >
            <PlusIcon className="h-4 w-4" /> Add
          </button>
        </div>
        <div className="p-3">
          <input
            type="text"
            placeholder="Search exams"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div className="flex-1 overflow-y-auto">
          {filteredExams.length === 0 && (
            <div className="text-gray-400 text-center mt-8">No exams found</div>
          )}
          {filteredExams.map((exam) => (
            <div
              key={exam.id}
              className={`flex items-center gap-3 px-4 py-3 cursor-pointer border-b hover:bg-teal-50 ${
                selectedExam && selectedExam.id === exam.id ? "bg-teal-100" : ""
              }`}
              onClick={() => {
                setSelectedExam(exam);
                setShowForm(false);
              }}
            >
              <div className="flex-shrink-0 w-10 h-10 bg-teal-200 rounded flex items-center justify-center text-lg font-bold text-teal-700">
                {exam.title[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{exam.title}</div>
                <div className="text-xs text-gray-500 truncate">
                  {exam.category}
                </div>
              </div>
              <button
                className="p-1 text-gray-400 hover:text-teal-600"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(exam);
                }}
                title="Edit"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
              <button
                className="p-1 text-gray-400 hover:text-red-600"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(exam.id);
                }}
                title="Delete"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Middle Column: Exam Details or Form */}
      <div className="w-2/4 min-w-[340px] bg-gray-50 flex flex-col">
        <div className="flex-1 overflow-y-auto p-8">
          {showForm ? (
            <form
              onSubmit={handleSubmit}
              className="bg-white shadow rounded-lg p-6 max-w-xl mx-auto"
            >
              <h2 className="text-xl font-semibold mb-4">
                {selectedExam ? "Edit Exam" : "Create New Exam"}
              </h2>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Category
                  </label>
                  <input
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Exam Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    name="examDate"
                    value={formData.examDate}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Duration (minutes)
                  </label>
                  <input
                    type="number"
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    min="1"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Mode
                  </label>
                  <select
                    name="mode"
                    value={formData.mode}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    required
                  >
                    <option value="Online">Online</option>
                    <option value="Offline">Offline</option>
                  </select>
                </div>
                {formData.mode === "Offline" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Location
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                      required
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Max Participants
                  </label>
                  <input
                    type="number"
                    name="maxParticipants"
                    value={formData.maxParticipants}
                    onChange={handleInputChange}
                    min="1"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    required
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setSelectedExam(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {selectedExam ? "Update Exam" : "Create Exam"}
                </button>
              </div>
            </form>
          ) : selectedExam ? (
            <div className="bg-white shadow rounded-lg p-8 max-w-xl mx-auto">
              <h2 className="text-2xl font-bold mb-2">{selectedExam.title}</h2>
              <div className="text-gray-500 mb-4">{selectedExam.category}</div>
              <div className="mb-4">{selectedExam.description}</div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <div className="text-xs text-gray-400">Date</div>
                  <div>
                    {new Date(selectedExam.examDate).toLocaleDateString()}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Time</div>
                  <div>
                    {new Date(selectedExam.examDate).toLocaleTimeString()}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Duration</div>
                  <div>{selectedExam.duration} min</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Mode</div>
                  <div>{selectedExam.mode}</div>
                </div>
                {selectedExam.mode === "Offline" && (
                  <div>
                    <div className="text-xs text-gray-400">Location</div>
                    <div>{selectedExam.location}</div>
                  </div>
                )}
                <div>
                  <div className="text-xs text-gray-400">Max Participants</div>
                  <div>{selectedExam.maxParticipants}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">
                    Current Participants
                  </div>
                  <div>{selectedExam.currentParticipants}</div>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => handleEdit(selectedExam)}
                  className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(selectedExam.id)}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400">
              Select an exam to view details
            </div>
          )}
        </div>
      </div>

      {/* Right Column: Exam Options (future: registrations, analytics, etc.) */}
      <div className="w-1/4 min-w-[260px] bg-white border-l flex flex-col">
        <div className="p-4 border-b font-semibold text-lg">Exam Options</div>
        <div className="p-4">
          {selectedExam ? (
            <ul className="space-y-3">
              <li className="text-teal-700 font-medium">Edit Exam Content</li>
              <li className="text-teal-700 font-medium">View Registrations</li>
              {/* Add more options as needed */}
            </ul>
          ) : (
            <div className="text-gray-400">Select an exam to see options</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Exams;
